import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Stack,
  Chip,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import DevIndexAPIs from '../../utilities/api/devIndexAPIs';


// TokenInput component for tokens/chips input
const TokenInput = ({
  tokens = [],
  setTokens,
  placeholder = 'Add value',
  disabled = false,
  single = false
}) => {
  const [input, setInput] = useState('')
  const tokensArray = Array.isArray(tokens) ? tokens : []

  const safeSetTokens = (newTokens) => {
    if (disabled) return
    if (typeof setTokens === 'function') {
      setTokens(newTokens)
    }
  }

  const addToken = (value) => {
    const v = (value || '').trim()
    if (!v || disabled) return
    if (single) {
      safeSetTokens([v])
      setInput('')
      return
    }
    if (tokensArray.includes(v)) {
      setInput('')
      return
    }
    safeSetTokens([...tokensArray, v])
    setInput('')
  }

  const handleKeyDown = (e) => {
    if (disabled) return
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addToken(input)
    }
  }

  const handlePaste = (e) => {
    if (disabled) return
    e.preventDefault()
    const paste = e.clipboardData.getData('text')
    const parts = paste
      .split(/[,;\n\r]+/)
      .map((p) => p.trim())
      .filter(Boolean)
    if (!parts.length) return
    if (single) {
      safeSetTokens([parts[0]])
      return
    }
    const uniqueParts = parts.filter((p) => !tokensArray.includes(p))
    if (uniqueParts.length) safeSetTokens([...tokensArray, ...uniqueParts])
  }

  const removeToken = (index) => {
    if (disabled) return
    if (single) {
      safeSetTokens([])
    } else {
      safeSetTokens(tokensArray.filter((_, i) => i !== index))
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.5,
          minHeight: 40
        }}
      >
        {tokensArray.map((token, i) => (
          <Chip
            key={i}
            label={token}
            size="small"
            onDelete={disabled ? undefined : () => removeToken(i)}
            color="primary"
            variant="outlined"
            sx={{ m: 0.25 }}
          />
        ))}
      </Box>
      {!disabled && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <Typography
            variant="div"
            color="text.secondary"
            sx={{ userSelect: 'none', fontSize: '0.8rem' }}
          >
            {single ? '(Single value)' : '(Press Enter or comma to add)'}
          </Typography>
          <input
            type="text"
            value={input}
            placeholder={placeholder}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            style={{
              minWidth: 120,
              marginTop: tokensArray.length ? 8 : 0,
              height: 32,
              padding: '4px 8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </Box>
      )}
    </Box>
  )
}

const FRTManagementContainer = () => {
  const user = useSelector((state) => state.user.user)
  const [devIndexData, setDevIndexData] = useState([])
  const [loading, setLoading] = useState(false)
  const [editingStates, setEditingStates] = useState({})
  const [successMessage, setSuccessMessage] = useState('')
  const [error, setError] = useState('')


  const fetchDevIndexData = useCallback(async () => {
    try {
      setLoading(true)
      const data = await DevIndexAPIs.get_dev_index()
      let sortedList = data.sort((a, b) => a.id - b.id)
      if (user.user?.TL_name !== 'MBF Admin') {
        sortedList = sortedList.filter(
          i => i.name?.toLowerCase() === user.user?.TL_name?.toLowerCase()
        );
      }
      setDevIndexData(sortedList)
      setError('')
    } catch (error) {
      setError('Failed to fetch FRT Management data')
      console.error('Error fetching FRT Management data:', error)
    } finally {
      setLoading(false)
    }
  }, [user?.user?.TL_name])

  // Fetch data on component mount
  useEffect(() => {
    fetchDevIndexData()
  }, [fetchDevIndexData])

  // Initialize editing states when data changes
  useEffect(() => {
    const initialStates = {}
    devIndexData.forEach((item) => {
      initialStates[item.id] = {
        isEditing: false,
        tempLdaps: [...(item.accepted_ldaps || [])],
        tempDevIndexes: [...(item.devIndexes || [])],
        tempTlName: item.name ? [item.name] : []
      }
    })
    setEditingStates(initialStates)
  }, [devIndexData])


  const updateEditingState = useCallback((id, updates) => {
    setEditingStates((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...updates }
    }))
  }, [])

  const handleEdit = useCallback((id) => {
    setDevIndexData((data) => {
      const item = data.find((item) => item.id === id)
      if (!item) return data
      setEditingStates((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          isEditing: true,
          tempLdaps: [...(item.accepted_ldaps || [])],
          tempDevIndexes: [...(item.devIndexes || [])]
        }
      }))
      return data
    })
  }, [])

  const handleCancel = useCallback((id) => {
    setDevIndexData((data) => {
      const item = data.find((item) => item.id === id)
      if (!item) return data
      setEditingStates((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          isEditing: false,
          tempLdaps: [...(item.accepted_ldaps || [])],
          tempDevIndexes: [...(item.devIndexes || [])],
          tempTlName: item.name ? [item.name] : []
        }
      }))
      return data
    })
  }, [])

  const handleSave = useCallback(async (id) => {
    setDevIndexData((currentData) => {
      const item = currentData.find((item) => item.id === id)
      if (!item) return currentData
      
      setEditingStates((currentStates) => {
        const state = currentStates[id]
        if (!state) return currentStates

        const payload = {
          name: state.tempTlName && state.tempTlName[0] ? state.tempTlName[0] : item.name || '',
          accepted_ldaps: Array.isArray(state.tempLdaps)
            ? state.tempLdaps
            : item.accepted_ldaps || [],
          devIndexes: Array.isArray(state.tempDevIndexes)
            ? state.tempDevIndexes
            : item.devIndexes || []
        }

        DevIndexAPIs.update_dev_index(id, payload)
          .then((response) => {
            if (response && response.status >= 200 && response.status < 300) {
              const updatedItem =
                response.data.data && typeof response.data.data === 'object'
                  ? response.data.data
                  : payload
              setDevIndexData((prev) => prev.map((it) => (it.id === id ? { ...it, ...updatedItem } : it)))
              setSuccessMessage('Changes saved successfully!')
              setEditingStates((prev) => ({
                ...prev,
                [id]: {
                  ...prev[id],
                  isEditing: false,
                  tempLdaps: [],
                  tempDevIndexes: [],
                  tempTlName: []
                }
              }))
            } else {
              const errMsg =
                response?.data?.error ||
                response?.data?.message ||
                response?.statusText ||
                'Update failed'
              setError(errMsg)
            }
          })
          .catch((error) => {
            let msg = 'Failed to save changes'
            if (error?.response) {
              msg =
                error.response.data?.error ||
                error.response.data?.message ||
                `Server error: ${error.response.status}`
            } else if (error?.request) {
              msg = 'Network error. Please check your connection.'
            } else if (error?.message) {
              msg = error.message
            }
            setError(msg)
            console.error('Error saving changes:', error)
          })

        return currentStates
      })
      return currentData
    })
  }, [])

  // Define columns for DataGrid - memoized to prevent recreating on every render
  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'id',
        headerName: 'ID',
        flex: 1,
        sortable: true
      },
      {
        field: 'name',
        headerName: 'TL Name',
        flex: 2,
        renderCell: (params) => {
          const state = editingStates[params.row.id] || {}
          return state.isEditing ? (
            <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              <TokenInput
                tokens={state.tempTlName || []}
                setTokens={(tokens) => updateEditingState(params.row.id, { tempTlName: tokens })}
                placeholder="Enter TL Name"
                single={true}
              />
            </Box>
          ) : (
            <TokenInput
              tokens={params.row.name ? [params.row.name] : []}
              placeholder=""
              disabled={true}
            />
          )
        }
      },
      {
        field: 'accepted_ldaps',
        headerName: 'LDAPs',
        flex: 3,
        renderCell: (params) => {
          const state = editingStates[params.row.id] || {}
          return state.isEditing ? (
            <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              <TokenInput
                tokens={state.tempLdaps || []}
                setTokens={(tokens) => updateEditingState(params.row.id, { tempLdaps: tokens })}
                placeholder="Enter LDAP"
              />
            </Box>
          ) : (
            <TokenInput
              tokens={params.row.accepted_ldaps || []}
              placeholder=""
              disabled={true}
            />
          )
        }
      },
      {
        field: 'devIndexes',
        headerName: 'FRTs',
        flex: 3,
        renderCell: (params) => {
          const state = editingStates[params.row.id] || {}
          return state.isEditing ? (
            <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              <TokenInput
                tokens={state.tempDevIndexes || []}
                setTokens={(tokens) => updateEditingState(params.row.id, { tempDevIndexes: tokens })}
                placeholder="Enter FRT"
              />
            </Box>
          ) : (
            <TokenInput
              tokens={params.row.devIndexes || []}
              placeholder=""
              disabled={true}
            />
          )
        }
      }
    ]

    // Add Actions column only for MBF Admin
    if (user.user.is_admin && user.user.TL_name === 'MBF Admin') {
      baseColumns.push({
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const state = editingStates[params.row.id] || {}
          return state.isEditing ? (
            <Stack direction="row" spacing={1}>
              <IconButton
                onClick={() => handleSave(params.row.id)}
                color="primary"
                size="small"
                title="Save Changes"
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                onClick={() => handleCancel(params.row.id)}
                color="secondary"
                size="small"
                title="Cancel"
              >
                <CancelIcon />
              </IconButton>
            </Stack>
          ) : (
            <IconButton
              onClick={() => handleEdit(params.row.id)}
              color="primary"
              size="small"
              title="Edit Row"
            >
              <EditIcon />
            </IconButton>
          )
        }
      })
    }

    return baseColumns
  }, [editingStates, user.user.is_admin, user.user.TL_name])

  return (
    <>
      <Box
        sx={{
        boxSizing: 'border-box',
        // width: 'border-box',
        height: 'auto',
        marginBlock: '1rem',
        borderRadius: 1,
        border: '#A6A6A6 solid 1px',
     
          width: '100%',
        //   height: 'auto',
        //   marginBlock: '1rem',
        //   borderRadius: 1,
        //   border: '#A6A6A6 solid 1px',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, minHeight: '60vh' }}>
            <CircularProgress />
          </Box>
        ) : devIndexData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3, minHeight: '60vh' }}>
            <Typography variant="body1" color="text.secondary">
              No FRT Management entries found
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', height: '70vh', width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
            <DataGrid
              rows={devIndexData}
              columns={columns}
              getRowId={(row) => row.id}
              getRowHeight={() => 'auto'}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 }
                }
              }}
              pageSizeOptions={[5, 10, 20]}
              disableSelectionOnClick
              disableVirtualization={true}
              slotProps={{
                scrollbar: {
                  hideDelay: 200,
                  orientation: 'vertical'
                }
              }}
              sx={{
                width: '100%',
                maxWidth: '100%',
                border: 'none',
                '& .MuiDataGrid-root': {
                  width: '100%',
                  maxWidth: '100%'
                },
                '& .MuiDataGrid-main': {
                  width: '100%',
                  maxWidth: '100%'
                },
                '& .MuiDataGrid-virtualScroller': {
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  }
                },
                '& .MuiDataGrid-columnHeaders': {
                  paddingRight: '0 !important',
                  width: '100%',
                  display: 'flex',
                  boxSizing: 'border-box'
                },
                '& .MuiDataGrid-columnHeadersInner': {
                  width: '100%',
                  display: 'flex',
                  boxSizing: 'border-box'
                },
                '& .MuiDataGrid-columnHeaderWrapper': {
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  boxSizing: 'border-box',
                  gap: 0
                },
                '& .MuiDataGrid-columnHeader': {
                  flex: '1 1 auto !important',
                  backgroundColor: '#f5f5f5',
                  borderBottom: '2px solid #e0e0e0',
                  minWidth: '0 !important'
                },
                '& .MuiDataGrid-row': {
                  paddingRight: '0 !important',
                  padding: '8px 12px !important'
                },
                '& .MuiDataGrid-footerContainer': {
                  justifyContent: 'flex-start'
                },
                '& .MuiTablePagination-root': {
                  width: '100%'
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  overflow: 'visible',
                  lineHeight: 'normal'
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 600,
                  color: '#212121'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#fafafa'
                }
              }}
            />
          </Box>
        )}
      </Box>

      {/* Success Message Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          zIndex: 99999
        }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          zIndex: 99999
        }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  )
}

export default FRTManagementContainer
